import { useEffect, useState } from "react";
import CustomTableWithPagination from "../../../reusable-components/CustomTable";
import {
  InvoiceDataType,
  RecallInvoiceDetailsType,
  UpdateInvoiceStatusType,
} from "../../../Types/invoiceManagementTypes";
import {
  InvoiceRecordStatus,
  QuickbookStatus,
  UpdateInvoiceStatusKey,
} from "../../../enums/InvoiceManagementEnums";
import {
  getInvoiceManagemnetTableColumns,
  getModalConstants,
  getTableFooter,
  tableSelection,
} from "./constants";
import styles from "./ListViewTable.module.scss";
import clsx from "clsx";
import InvoiceManagementServices from "../../../services/invoiceManagement.service";
import { message, Modal } from "antd";
import TNButton from "../../../reusable-components/TNButton";
import { ButtonType } from "../../../enums/ButtonAttributesEnums";
import { ModalButtonStyle } from "../../../constants/CustomStyles";
import SuccessModal from "../../../reusable-components/SuccessModal";
import InvoicePreviewTemplate from "../../InvoicePreviewTemplate";
import TNLoader from "../../../reusable-components/TNLoader";
import { downloadUtil } from "../../../utils/downloadUtil";
import ConditionalRender from "../../../reusable-components/ConditionalRender";
import SocketLoader from "../../../reusable-components/SocketLoader";
import { socketActionType } from "../../../enums/socketEnums";
import RecallConfirmationModal from "./RecallConfirmationModal";
import JobDatabaseService from "../../../services/jobDatabase.service";
import { storageKeys } from "../../../constants/storageKeys";
import { ApiResponse } from "../../../enums/ApiResponse";

interface ListViewTableProps {
  searchVal?: string;
  dateRange?: object;
  filters?: object;
  trackSelectedKeys: () => void;
  trackSelectAll: () => void;
  trackTotalRecord: React.Dispatch<React.SetStateAction<number>>;
  trackUnselectedKeys: React.Dispatch<React.SetStateAction<number>>;
  editAccess?: boolean;
  bulkUpdateKey?: number;
}

interface sortType {
  sortBy: string | undefined;
  sortOrder: string | undefined;
}

const ListViewTable = (props?: ListViewTableProps) => {
  const {
    searchVal,
    dateRange,
    filters,
    trackSelectedKeys,
    trackSelectAll,
    trackTotalRecord,
    trackUnselectedKeys,
    editAccess = true,
    bulkUpdateKey,
  } = props;
  const { GetPaginationDefaultMeta } = JobDatabaseService();
  const {
    FetchListViewData,
    UpdateInvoiceStatus,
    ArchiveInvoiceAction,
    GetPreSignedInvoiceUrl,
    BulkStatusUpdate,
    GetRecallInvoiceDetails,
    RecallSelectedInvoiceBatch,
    DownloadOriginalInvoiceTickets,
  } = InvoiceManagementServices();
  const { data: defaultData, isFetching: gettingDefaults } =
    GetPaginationDefaultMeta();
  const {
    mutateAsync: updateInvoiceStatus,
    isPending: isUpdateInvoiceStatusPending,
  } = UpdateInvoiceStatus();
  const {
    mutateAsync: archiveInvoiceAction,
    isPending: isArchiveActionPending,
  } = ArchiveInvoiceAction();
  const {
    mutateAsync: getPreSignedInvoiceUrl,
    data: presignedUrlData,
    isPending: isFetchingUrlPending,
  } = GetPreSignedInvoiceUrl();
  const {
    mutateAsync: bulkStatusUpdate,
    isPending: isBulkStatusUpdatePending,
  } = BulkStatusUpdate();
  const {
    mutateAsync: getRecallInvoiceDetails,
    isPending: getRecallInvoiceDetailsPending,
  } = GetRecallInvoiceDetails();
  const { mutateAsync: initiateRecallInvoice } = RecallSelectedInvoiceBatch();
  const { mutateAsync: downloadOriginalInvoiceFile } =
    DownloadOriginalInvoiceTickets();

  const [pageSizeChanged, setPageSizeChanged] = useState<boolean>(false);
  const [pageSize, setPageSize] = useState<number>(
    Number(sessionStorage.getItem(storageKeys.INVOICE_LIST_PAGESIZE) ?? 10),
  );
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [allPagesSelected, setAllPagesSelected] = useState(false);
  const [unselectedRowKeys, setUnselectedRowKeys] = useState([]);
  const [isQuickbookStatusChangeModalOpen, setQuickbookStatusChangeModalOpen] =
    useState(false);
  const [isArchiveInvoiceModalOpen, setArchiveInvoiceModalOpen] =
    useState(false);
  const [isSuccessModalOpen, setSuccessModalOpen] = useState<boolean>(false);
  const [currentSyncStatus, setCurrentSyncStatus] = useState<QuickbookStatus>(
    QuickbookStatus.SYNCED,
  );
  const [syncStatusLoading, setSyncStatusLoading] = useState<boolean>(false);
  const [nonSyncStatusLoading, setNonSyncStatusLoading] =
    useState<boolean>(false);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState<boolean>(false);
  const [currentInvoiceId, setCurrentInvoiceId] = useState<string>("");
  const [currentInvoiceDisplayId, setCurrentInvoiceDisplayId] =
    useState<string>("");
  const [isBulkStatusModalOpen, setIsBulkStatusModalOpen] =
    useState<boolean>(false);
  const [isBulkQbStatusModalOpen, setIsBulkQbStatusModalOpen] =
    useState<boolean>(false);
  const [pendingStatusChange, setPendingStatusChange] =
    useState<UpdateInvoiceStatusType | null>(null);
  const [bulkStatusLoading, setBulkStatusLoading] = useState<boolean>(false);
  const [recallInvoiceDetails, setRecallInvoiceDetails] = useState<
    RecallInvoiceDetailsType | undefined
  >({});
  const [isRecallConfirmationOpen, setIsRecallConfirmationOpen] =
    useState<boolean>(false);
  const [recallAction, setRecallAction] = useState<boolean>(false);
  const [downloadOriginalFile, setDownloadOriginalFile] =
    useState<boolean>(false);
  const [sortFilter, setSortFilter] = useState<sortType | undefined>(undefined);
  const [isDownloading, setIsDownloading] = useState(false);

  const {
    data: mockData,
    isLoading,
    refetch,
    isSuccess: listFetchSuccess,
  } = FetchListViewData(
    {
      page: currentPage,
      perPage: pageSize,
      search: searchVal,
      startDate: dateRange?.startDate,
      endDate: dateRange?.endDate,
      ...filters,
      ...sortFilter,
    },
    pageSizeChanged,
    !!sessionStorage.getItem(storageKeys.INVOICE_LIST_PAGESIZE),
  );

  const openQuickbookStatusChangeModal = () =>
    setQuickbookStatusChangeModalOpen(true);
  const closeQuickbookStatusChangeModal = () =>
    setQuickbookStatusChangeModalOpen(false);
  const openArchiveInvoiceModal = (invoiceId?: string) => {
    setCurrentInvoiceId(invoiceId);
    setArchiveInvoiceModalOpen(true);
  };
  const closeArchiveInvoiceModal = () => setArchiveInvoiceModalOpen(false);
  const openSuccessModal = () => setSuccessModalOpen(true);
  const closeSuccessModal = () => setSuccessModalOpen(false);
  const openPreviewModal = () => setIsPreviewModalOpen(true);
  const closePreviewModal = () => setIsPreviewModalOpen(false);
  const openBulkStatusModal = () => setIsBulkStatusModalOpen(true);
  const closeBulkStatusModal = () => {
    setIsBulkStatusModalOpen(false);
    setPendingStatusChange(null);
  };
  const openBulkQbStatusModal = () => setIsBulkQbStatusModalOpen(true);
  const closeBulkQbStatusModal = () => {
    setIsBulkQbStatusModalOpen(false);
    setPendingStatusChange(null);
  };
  const openRecallConfirmation = () => setIsRecallConfirmationOpen(true);
  const closeRecallConfirmation = () => setIsRecallConfirmationOpen(false);
  const cancelRecallConfirmation = () => {
    setCurrentInvoiceId(undefined);
    setRecallInvoiceDetails(undefined);
  };

  const handleRecallStart = () => {
    setRecallAction(true);
  };
  const handleRecallEnd = () => {
    setCurrentInvoiceId(undefined);
    setRecallAction(false);
  };
  const handleRecallSuccess = () => refetch();

  const handleDownloadFileStart = () => setDownloadOriginalFile(true);
  const handleDownloadFileEnd = () => {
    setCurrentInvoiceId(undefined);
    setDownloadOriginalFile(false);
  };

  const getInvoiceDetailsForRecall = async (invoiceId: string) => {
    const data = await getRecallInvoiceDetails(invoiceId, {
      onSuccess: (data) => {
        setRecallInvoiceDetails(data);
        openRecallConfirmation();
      },
      onError: (err) => {
        message.error(err);
        setRecallInvoiceDetails({});
        openRecallConfirmation();
      },
    });
  };

  const handleRecallAction = (invoiceId: string) => {
    setCurrentInvoiceId(invoiceId);
    getInvoiceDetailsForRecall(invoiceId);
  };

  const handleDownloadFile = (invoiceId: string) => {
    setCurrentInvoiceId(invoiceId);
    handleDownloadFileStart();
  };

  const initateInvoiceRecallAction = async () => {
    await initiateRecallInvoice(currentInvoiceId);
  };

  const initiateDownloadOriginalFile = async () => {
    await downloadOriginalInvoiceFile(currentInvoiceId);
  };

  const handleQbStatusChange = async (status: QuickbookStatus) => {
    if (status === QuickbookStatus.SYNCED) setSyncStatusLoading(true);
    else setNonSyncStatusLoading(true);
    await updateInvoiceStatus(
      {
        invoiceId: currentInvoiceId,
        key: UpdateInvoiceStatusKey.SYNC_STATUS,
        status: status,
      },
      {
        onSuccess: () => {
          if (status === QuickbookStatus.SYNCED) setSyncStatusLoading(false);
          else setNonSyncStatusLoading(false);
          closeQuickbookStatusChangeModal();
          openSuccessModal();
        },
        onError: () => {
          if (status === QuickbookStatus.SYNCED) setSyncStatusLoading(false);
          else setNonSyncStatusLoading(false);
          closeQuickbookStatusChangeModal();
          message.error(FAILURE_CONTENT);
        },
      },
    );
  };

  const {
    WIDTH,
    QUICKBOOK: {
      QB_TITLE,
      MARK_SYNCED_LABEL,
      MARK_UNSYNCED_LABEL,
      CONFIRMATION_CONTENT,
      SUCCESS_CONTENT,
      FAILURE_CONTENT,
    },
    ARCHIVE: {
      ARCHIVE_TITLE,
      ARCHIVE_CONTENT,
      ARCHIVE_OK_LABEL,
      ARCHIVE_CANCEL_LABEL,
      ARCHIVE_SUCCESS,
      ARCHIVE_FAILURE,
    },
    INVOICE_FAILURE,
    DOWNLOAD_INVOICE_FAILURE,
    BULK_STATUS: {
      STATUS_CHANGE_TITLE,
      STATUS_UPDATE_SUCCESS,
      STATUS_UPDATE_FAILURE,
      QB_UPDATE_SUCCESS,
      QB_UPDATE_FAILURE,
      UPDATE_ALL,
    },
  } = getModalConstants;

  const {
    SELECT_ALL: { SELECT_ALL_KEY, SELECT_ALL_LABEL },
    DESELECT_ALL: { DESELECT_ALL_KEY, DESELECT_ALL_LABEL },
  } = tableSelection;

  const qbModalFooter = (
    <div>
      <TNButton
        type={ButtonType.DEFAULT}
        text={MARK_UNSYNCED_LABEL}
        customStyles={ModalButtonStyle}
        onClick={() => handleQbStatusChange(QuickbookStatus.NOT_SYNCED)}
        loading={nonSyncStatusLoading && isUpdateInvoiceStatusPending}
      />
      <TNButton
        type={ButtonType.DEFAULT}
        text={MARK_SYNCED_LABEL}
        customStyles={ModalButtonStyle}
        onClick={() => handleQbStatusChange(QuickbookStatus.SYNCED)}
        loading={syncStatusLoading && isUpdateInvoiceStatusPending}
      />
    </div>
  );

  const getSelectedCount = () => {
    if (allPagesSelected) {
      const totalRecords = mockData?.meta?.totalRecords || 0;
      return totalRecords - unselectedRowKeys?.length;
    }
    return selectedRowKeys?.length;
  };

  const handleSingleStatusUpdate = async () => {
    if (!pendingStatusChange) return;

    setBulkStatusLoading(true);
    await updateInvoiceStatus(pendingStatusChange, {
      onSuccess: () => {
        setBulkStatusLoading(false);
        closeBulkStatusModal();
        message.success(STATUS_UPDATE_SUCCESS);
        refetch();
      },
      onError: () => {
        setBulkStatusLoading(false);
        closeBulkStatusModal();
        message.error(STATUS_UPDATE_FAILURE);
      },
    });
  };

  const handleSingleQbStatusUpdate = async () => {
    if (!pendingStatusChange) return;

    setBulkStatusLoading(true);
    await updateInvoiceStatus(pendingStatusChange, {
      onSuccess: () => {
        setBulkStatusLoading(false);
        closeBulkQbStatusModal();
        message.success(QB_UPDATE_SUCCESS);
        refetch();
      },
      onError: () => {
        setBulkStatusLoading(false);
        closeBulkQbStatusModal();
        message.error(QB_UPDATE_FAILURE);
      },
    });
  };

  const handleBulkStatusUpdate = async () => {
    if (!pendingStatusChange) return;

    setBulkStatusLoading(true);
    const selectedData = getActualSelectedKeys();

    await bulkStatusUpdate(
      {
        filters: {
          ...filters,
          invoiceIds: selectedData?.allSelected
            ? selectedData?.unselectedKeys
            : selectedData?.selectedKeys,
          selectAll: selectedData?.allSelected,
        },
        statusFields: {
          fromExport: false,
          type: UpdateInvoiceStatusKey?.INVOICE_STATUS,
          status: pendingStatusChange?.status,
        },
      },
      {
        onSuccess: () => {
          setBulkStatusLoading(false);
          closeBulkStatusModal();
          message.success(STATUS_UPDATE_SUCCESS);
        },
        onError: () => {
          message.error(STATUS_UPDATE_FAILURE);
        },
      },
    );
  };

  const handleBulkQbStatusUpdate = async () => {
    if (!pendingStatusChange) return;

    setBulkStatusLoading(true);
    const selectedData = getActualSelectedKeys();

    await bulkStatusUpdate(
      {
        filters: {
          ...filters,
          invoiceIds: selectedData?.allSelected
            ? selectedData?.unselectedKeys
            : selectedData?.selectedKeys,
          selectAll: selectedData?.allSelected,
        },
        statusFields: {
          fromExport: false,
          type: UpdateInvoiceStatusKey?.SYNC_STATUS,
          status: pendingStatusChange?.status,
        },
      },
      {
        onSuccess: () => {
          setBulkStatusLoading(false);
          closeBulkQbStatusModal();
          message.success(QB_UPDATE_SUCCESS);
        },
        onError: () => {
          message.error(QB_UPDATE_FAILURE);
        },
      },
    );
  };

  const handleStatusChange = async (params?: UpdateInvoiceStatusType) => {
    const selectedCount = getSelectedCount();
    if (selectedCount >= 2) {
      const invoice = mockData?.data?.find(
        (item) => item.id === params?.invoiceId,
      );
      setCurrentInvoiceDisplayId(invoice?.invoiceId || params?.invoiceId);
      setPendingStatusChange(params);

      if (params?.key === UpdateInvoiceStatusKey.SYNC_STATUS) {
        openBulkQbStatusModal();
      } else {
        openBulkStatusModal();
      }
    } else {
      if (params?.key === UpdateInvoiceStatusKey.SYNC_STATUS) {
        setCurrentSyncStatus(params?.status);
        setCurrentInvoiceId(params?.invoiceId);
        openQuickbookStatusChangeModal();
      } else {
        await updateInvoiceStatus({ ...params });
      }
    }
  };

  const handleFetchingPresignedUrl = async (invoiceId?: string) => {
    await getPreSignedInvoiceUrl(invoiceId, {
      onSuccess: openPreviewModal,
      onError: () => message.error(INVOICE_FAILURE),
    });
  };

  const handleInvoicePDFDownload = async (invoiceId?: string) => {
    setIsDownloading(true);
    await getPreSignedInvoiceUrl(invoiceId, {
      onSuccess: async (data) => {
        await downloadUtil(data?.url);
      },
      onSettled: () => {
        setIsDownloading(false);
      },
      onError: () => message.error(DOWNLOAD_INVOICE_FAILURE),
    });
  };

  const columns = getInvoiceManagemnetTableColumns(
    handleStatusChange,
    openArchiveInvoiceModal,
    handleInvoicePDFDownload,
    editAccess,
    handleRecallAction,
    handleDownloadFile,
    sortFilter,
  );

  const handleCurrentPage = (page: number) => {
    setCurrentPage(page);
  };
  const handlePagination = (page: number) => handleCurrentPage(page);
  const handlePaginationPageSize = (size: number) => {
    setPageSizeChanged(true);
    setPageSize(size);
    setCurrentPage(1);
    try {
      sessionStorage.setItem(storageKeys.INVOICE_LIST_PAGESIZE, size);
    } catch (err) {
      console.error("Err", err);
    }
  };

  const handleRowSelectionChange = (selectedRowKeys, selectedRows) => {
    if (allPagesSelected) {
      const currentPageKeys = (mockData?.data || [])?.map(
        (row) => row?.invoiceId,
      );
      const currentPageUnselected = currentPageKeys?.filter(
        (key) => !selectedRowKeys?.includes(key),
      );

      const newUnselectedKeys = [...unselectedRowKeys];

      selectedRowKeys?.forEach((key) => {
        const index = newUnselectedKeys?.indexOf(key);
        if (index > -1) {
          newUnselectedKeys?.splice(index, 1);
        }
      });

      currentPageUnselected?.forEach((key) => {
        if (!newUnselectedKeys?.includes(key)) {
          newUnselectedKeys?.push(key);
        }
      });

      setUnselectedRowKeys(newUnselectedKeys);
      trackSelectedKeys(newUnselectedKeys);
      trackUnselectedKeys(newUnselectedKeys?.length);
    } else {
      setSelectedRows(selectedRows);
      setSelectedRowKeys(selectedRowKeys);
      trackSelectedKeys(selectedRowKeys);
      setUnselectedRowKeys([]);
      trackUnselectedKeys(0);
    }
  };

  const handleSelectAllPages = () => {
    setAllPagesSelected(true);
    trackSelectAll(true);
    setUnselectedRowKeys([]);
    trackUnselectedKeys(0);
    setSelectedRows([]);
    setSelectedRowKeys([]);
    trackSelectedKeys([]);
  };

  const getEffectiveSelectedKeysForCurrentPage = () => {
    if (allPagesSelected) {
      const currentPageKeys = (mockData?.data || [])?.map(
        (row) => row?.invoiceId,
      );
      return currentPageKeys?.filter(
        (key) => !unselectedRowKeys?.includes(key),
      );
    }
    return selectedRowKeys;
  };

  const getTotalSelectedCount = () => {
    if (allPagesSelected) {
      const totalRecords = mockData?.meta?.totalRecords || 0;
      return totalRecords - unselectedRowKeys?.length;
    }
    return selectedRows.length;
  };

  const getActualSelectedKeys = () => {
    if (allPagesSelected) {
      return { allSelected: true, unselectedKeys: unselectedRowKeys };
    }
    return { allSelected: false, selectedKeys: selectedRowKeys };
  };

  const handleDeselectAll = () => {
    setSelectedRows([]);
    setSelectedRowKeys([]);
    trackSelectedKeys([]);
    setAllPagesSelected(false);
    trackSelectAll(false);
    setUnselectedRowKeys([]);
    trackUnselectedKeys(0);
  };

  const rowselection = {
    onChange: handleRowSelectionChange,
    preserveSelectedRowKeys: true,

    selectedRowKeys: getEffectiveSelectedKeysForCurrentPage(),
    selections: [
      {
        key: SELECT_ALL_KEY,
        text: SELECT_ALL_LABEL,
        onSelect: handleSelectAllPages,
      },
      {
        key: DESELECT_ALL_KEY,
        text: DESELECT_ALL_LABEL,
        onSelect: handleDeselectAll,
      },
    ],
    onSelectAll: (selected, selectedRows, changeRows) => {
      if (allPagesSelected) {
        const currentPageKeys = (mockData?.data || []).map(
          (row) => row?.invoiceId,
        );

        if (selected) {
          const newUnselectedKeys = unselectedRowKeys?.filter(
            (key) => !currentPageKeys?.includes(key),
          );
          setUnselectedRowKeys(newUnselectedKeys);
          trackUnselectedKeys(newUnselectedKeys?.length);
        } else {
          const newUnselectedKeys = [...unselectedRowKeys];
          currentPageKeys?.forEach((key) => {
            if (!newUnselectedKeys?.includes(key)) {
              newUnselectedKeys?.push(key);
            }
          });
          setUnselectedRowKeys(newUnselectedKeys);
          trackUnselectedKeys(newUnselectedKeys?.length);
        }
      } else {
        if (selected) {
          const currentPageRows = mockData?.data || [];
          const newSelectedKeys = [...selectedRowKeys];
          const newSelectedRows = [...selectedRows];

          currentPageRows?.forEach((row) => {
            if (!newSelectedKeys?.includes(row.invoiceId)) {
              newSelectedKeys?.push(row.invoiceId);
              newSelectedRows?.push(row);
            }
          });

          setSelectedRowKeys(newSelectedKeys);
          trackSelectedKeys(newSelectedKeys);
          setSelectedRows(newSelectedRows);
        } else {
          const currentPageKeys = (mockData?.data || []).map(
            (row) => row?.invoiceId,
          );
          const newSelectedKeys = selectedRowKeys.filter(
            (key) => !currentPageKeys?.includes(key),
          );
          const newSelectedRows = selectedRows.filter(
            (row) => !currentPageKeys?.includes(row.invoiceId),
          );

          setSelectedRowKeys(newSelectedKeys);
          trackSelectedKeys(newSelectedKeys);
          setSelectedRows(newSelectedRows);
        }
      }
    },
  };

  const getRowClassName = (record: InvoiceDataType) => {
    return clsx({
      [styles.StatusErrorRow]: record.status === InvoiceRecordStatus.ERROR,
      [styles.StatusWarnRow]: record.status === InvoiceRecordStatus.WARN,
      [styles.MinimumHoursRow]: !!record?.usedMinimumHours,
    });
  };

  const handleSorter = (sorter) => {
    if (!sorter?.order) {
      setSortFilter({
        sortBy: undefined,
        sortOrder: undefined,
      });
      return;
    }
    setSortFilter({
      sortBy: sorter?.columnKey,
      sortOrder: sorter?.order,
    });
  };

  const totalRecords = mockData?.meta?.totalRecords;

  useEffect(() => {
    refetch();
  }, [bulkUpdateKey]);

  useEffect(() => {
    setSelectedRows([]);
    setSelectedRowKeys([]);
    trackSelectedKeys([]);
    trackSelectAll(false);
    setAllPagesSelected(false);
    setCurrentPage(1);
  }, [searchVal, filters, dateRange]);

  useEffect(() => {
    if (typeof totalRecords === "number") {
      trackTotalRecord(totalRecords);
    }
  }, [totalRecords, trackTotalRecord]);

  useEffect(() => {
    if (defaultData?.message && defaultData?.message === ApiResponse.SUCCESS) {
      setPageSize(defaultData?.data?.invoiceListDefaultPageSize);
    }
  }, [defaultData]);

  useEffect(() => {
    if (pageSizeChanged && listFetchSuccess) {
      setPageSizeChanged(false);
    }
  }, [pageSizeChanged, listFetchSuccess]);

  const selectedCount = getSelectedCount();

  return (
    <>
      <TNLoader
        isLoading={
          getRecallInvoiceDetailsPending ||
          isFetchingUrlPending ||
          isDownloading
        }
      />
      <InvoicePreviewTemplate
        isOpen={isPreviewModalOpen}
        handleClose={closePreviewModal}
        url={presignedUrlData?.url}
        editAccess={editAccess}
      />
      <SuccessModal
        isWarningModalOpen={isSuccessModalOpen}
        closeHandler={closeSuccessModal}
        message={SUCCESS_CONTENT}
      />
      <Modal
        visible={isBulkStatusModalOpen}
        title={STATUS_CHANGE_TITLE}
        width={WIDTH}
        closable
        onCancel={closeBulkStatusModal}
        className={styles.CustomModal}
        footer={
          <div>
            <TNButton
              type={ButtonType.DEFAULT}
              text={`Update only ${currentInvoiceDisplayId}`}
              customStyles={ModalButtonStyle}
              onClick={handleSingleStatusUpdate}
            />
            <TNButton
              type={ButtonType.PRIMARY}
              text={UPDATE_ALL}
              onClick={handleBulkStatusUpdate}
            />
          </div>
        }
      >
        {`Change status for all ${selectedCount} selected invoices or for only Invoice ${currentInvoiceDisplayId}?`}
      </Modal>
      <Modal
        visible={isBulkQbStatusModalOpen}
        title={STATUS_CHANGE_TITLE}
        width={WIDTH}
        closable
        onCancel={closeBulkQbStatusModal}
        className={styles.CustomModal}
        footer={
          <div>
            <TNButton
              type={ButtonType.DEFAULT}
              text={`Update only ${currentInvoiceDisplayId}`}
              customStyles={ModalButtonStyle}
              onClick={handleSingleQbStatusUpdate}
            />
            <TNButton
              type={ButtonType.PRIMARY}
              text={UPDATE_ALL}
              onClick={handleBulkQbStatusUpdate}
            />
          </div>
        }
      >
        {`Change Quickbook status for all ${selectedCount} selected invoices or for only Invoice ${currentInvoiceDisplayId}?`}
      </Modal>

      <Modal
        visible={isArchiveInvoiceModalOpen}
        title={ARCHIVE_TITLE}
        width={WIDTH}
        closable
        onCancel={closeArchiveInvoiceModal}
        className={styles.CustomModal}
        okText={ARCHIVE_OK_LABEL}
        okButtonProps={{
          title: ARCHIVE_OK_LABEL,
          loading: isArchiveActionPending,
          onClick: async () => {
            await archiveInvoiceAction(currentInvoiceId, {
              onSuccess: () => {
                closeArchiveInvoiceModal();
                message.success(ARCHIVE_SUCCESS);
              },
              onError: () => {
                closeArchiveInvoiceModal();
                message.error(ARCHIVE_FAILURE);
              },
            });
          },
        }}
        cancelButtonProps={{
          title: ARCHIVE_CANCEL_LABEL,
          onClick: closeArchiveInvoiceModal,
        }}
      >
        {ARCHIVE_CONTENT}
      </Modal>
      <Modal
        visible={isQuickbookStatusChangeModalOpen}
        title={QB_TITLE}
        width={WIDTH}
        closable
        onCancel={closeQuickbookStatusChangeModal}
        footer={qbModalFooter}
        className={styles.CustomModal}
      >
        {CONFIRMATION_CONTENT}
        {currentSyncStatus}
      </Modal>
      <ConditionalRender visible={recallAction}>
        <SocketLoader
          actionType={socketActionType.RECALL_INVOICE}
          handleActionCall={initateInvoiceRecallAction}
          closeRender={handleRecallEnd}
          handleCallback={handleRecallSuccess}
        />
      </ConditionalRender>
      <ConditionalRender visible={downloadOriginalFile}>
        <SocketLoader
          actionType={socketActionType.DOWNLOAD_ORIGINAL}
          handleActionCall={initiateDownloadOriginalFile}
          closeRender={handleDownloadFileEnd}
        />
      </ConditionalRender>
      <RecallConfirmationModal
        isOpen={isRecallConfirmationOpen}
        handleClose={closeRecallConfirmation}
        handleCancel={cancelRecallConfirmation}
        handleConfirm={handleRecallStart}
        invoiceDetails={recallInvoiceDetails}
      />
      <CustomTableWithPagination
        loading={isLoading}
        dataSource={mockData?.data ?? []}
        columns={columns}
        rowClassName={getRowClassName}
        className={styles.Table}
        pageSize={pageSize}
        currentPage={currentPage}
        rowSelection={rowselection}
        rowKey={(record) => record?.invoiceId}
        handleSizeChangerChange={handlePaginationPageSize}
        handleChange={handlePagination}
        totalData={mockData?.meta?.totalRecords}
        isClickableTable
        handleSelectedRow={async (record) =>
          await handleFetchingPresignedUrl(record?.id)
        }
        isSortable={true}
        handleSorter={handleSorter}
        footer={() =>
          getTableFooter(mockData?.nonSyncedCount, mockData?.meta?.totalRecords)
        }
      />
    </>
  );
};

export default ListViewTable;
